import { useEffect } from "react";
import { useAppSelector } from "../../hooks/useAppSelector";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import TableView, { ColumnConfig, FetchParams } from "../../components/controls/table-view";
import {
  ProductContainer,
  ProductImage,
  ProductNameContainer,
  ProductTitle,
} from "../../styles/shared/product";
import styled from "styled-components";
import {
  fetchOrderLines,
  resetOrderLines,
  setNewValue
} from "../../store/features/ordering/order-slice";
import Image from "../../components/controls/image";
import {
  GoogleIcon,
  ICON_ID,
} from "../../components/icons/google-icon";
import {
  SmallTextContainer,
  TableContainer,
  TextContainer,
} from "../../styles/shared/table";
import TextInput from "../../components/controls/text-input";
import { generateStatusIcon } from "../../helpers/status-helper";
import { currencyFormatter } from "../../helpers/formatters";

export default function OrderView() {
  const order = useAppSelector((state) => state.order);
  const dispatch = useAppDispatch();
  const deliveryMode = order.deliveryMode;

  const fetchMoreData = async (fetchParams: FetchParams) => {
    if (order.order?.id) {
      await dispatch(fetchOrderLines({ orderId: order.order.id, fetchParams }));
    }
  };

  useEffect(() => {
    fetchMoreData({
      reset: true
    });

    return () => {
      dispatch(resetOrderLines())
    }
  }, [order.order?.id]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (deliveryMode) {
        event.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [order, deliveryMode]);

  const columnConfig: { [key: string]: ColumnConfig } = {
    id: {
      hidden: true,
    },
    productId: {
      hidden: true,
    },
    productDescription: {
      hidden: true,
    },
    productSize: {
      hidden: true,
    },
    productImage: {
      hidden: true,
    },
    plu: {
      hidden: true,
    },
    lastOrdered: {
      hidden: true,
    },
    sizeUnit: {
      hidden: true
    },
    size: {
      hidden: true
    },
    status: {
      hidden: true,
    },
    approvalStatus: {
      hidden: true,
    },
    supplierId: {
      hidden: true
    },
    children: {
      hidden: true
    },
    orderId: {
      hidden: true
    },
    productName: {
      order: 1,
      name: "Line item",
      colspan: 4,
      render: (value: number, row: any, parentRow: any) => (
        <OrderProductContainer>
          {generateStatusIcon(row.approvalStatus ?? parentRow.approvalStatus)}
          <ProductImage>
            <Image image={row.productImage ?? parentRow.productImage}></Image>
          </ProductImage>
          <OrderProductNameContainer>
            <ProductTitle>
              {value}
            </ProductTitle>
            <div>
              {row.size} {row.sizeUnit} - {row.productDescription ?? parentRow.productDescription}
            </div>
            <SmallTextContainer>Plu: {row.plu ?? parentRow.plu}</SmallTextContainer>
            <MetaPillGroup>
              <MetaPill>
                <span>Supplier</span>
                <strong>{row.supplierName ?? parentRow?.supplierName}</strong>
              </MetaPill>
              <MetaPill>
                <span>Ordered</span>
                <strong>{row.quantity ?? 0}</strong>
              </MetaPill>
              <MetaPill>
                <span>Cost</span>
                <strong>{row.openPricing ? "TBC" : currencyFormatter.format(row.costPrice ?? parentRow?.costPrice ?? 0)}</strong>
              </MetaPill>
              <MetaPill>
                <span>Stock</span>
                <strong>{row.currentStockLevel ?? parentRow?.currentStockLevel ?? 0}</strong>
              </MetaPill>
            </MetaPillGroup>

            <DeliveryPillRow>
                <DeliveryPill>
                <span>Delivered</span>
                {deliveryMode ? (
                  <TextInput
                    label="Delivered qty"
                    type="number"
                    id={`Quantity-Received-input-${row.id}`}
                    value={(row.quantityReceived as string) ?? "0"}
                    onChange={(event) =>
                      dispatch(
                        setNewValue({
                          lineId: row.id,
                          parentLineId: parentRow?.id,
                          field: "quantityReceived",
                          value: event.target.value,
                        })
                      )
                    }
                    sx={{
                      marginTop: "0px",
                      marginBottom: "0px",
                      "& .MuiInputBase-root": {
                        height: "34px",
                        borderRadius: "7px",
                        backgroundColor: "rgba(255,255,255,0.95)",
                      },
                      "& .MuiInputLabel-root": {
                        display: "none",
                      },
                    }}
                  />
                ) : (
                  <strong>{row.quantityReceived ?? "0"}</strong>
                )}
              </DeliveryPill>
              <DeliveryPill $wide>
                <span>Delivery notes</span>
                {deliveryMode ? (
                  <TextInput
                    label="Delivery notes"
                    type="text"
                    id={`Delivery-Note-input-${row.id}`}
                    value={(row.deliveryNotes as string) ?? ""}
                    onChange={(event) =>
                      dispatch(
                        setNewValue({
                          lineId: row.id,
                          parentLineId: parentRow?.id,
                          field: "deliveryNotes",
                          value: event.target.value,
                        })
                      )
                    }
                    sx={{
                      marginTop: "0px",
                      marginBottom: "0px",
                      "& .MuiInputBase-root": {
                        minHeight: "34px",
                        borderRadius: "7px",
                        backgroundColor: "rgba(255,255,255,0.95)",
                      },
                      "& .MuiInputLabel-root": {
                        display: "none",
                      },
                    }}
                  />
                ) : (
                  <strong>{row.deliveryNotes || "-"}</strong>
                )}
              </DeliveryPill>
            </DeliveryPillRow>

            {(row.children?.length ?? 0) > 0 && (
              <SplitGroup>
                <SplitGroupTitle>
                  <GoogleIcon id={ICON_ID.Split} isSecondary />
                   Order Splits
                </SplitGroupTitle>
                {row.children.map((split: any) => (
                  <SplitCard key={`split-card-${row.id}-${split.id}`}>
                    <SplitMarker>
                      <GoogleIcon id={ICON_ID.Split} isSecondary />
                      Split
                    </SplitMarker>

                    <OrderProductContainer>
                      <ProductImage>
                        <Image image={row.productImage}></Image>
                      </ProductImage>
                      <OrderProductNameContainer>
                        <ProductTitle>{row.productName}</ProductTitle>
                        <div>
                          {split.size} {split.sizeUnit} - {row.productDescription}
                        </div>
                        <SmallTextContainer>Plu: {row.plu}</SmallTextContainer>

                        <MetaPillGroup>
                          <MetaPill>
                            <span>Supplier</span>
                            <strong>{split.supplierName ?? row.supplierName}</strong>
                          </MetaPill>
                          <MetaPill>
                            <span>Ordered</span>
                            <strong>{split.quantity ?? 0}</strong>
                          </MetaPill>
                          <MetaPill>
                            <span>Cost</span>
                            <strong>{row.openPricing ? "TBC" : currencyFormatter.format(split.costPrice ?? row.costPrice ?? 0)}</strong>
                          </MetaPill>
                        </MetaPillGroup>

                        <DeliveryPillRow>
                          <DeliveryPill>
                            <span>Delivered</span>
                            {deliveryMode ? (
                              <TextInput
                                label="Split delivered"
                                type="number"
                                id={`Split-Quantity-Received-input-${row.id}-${split.id}`}
                                value={(split.quantityReceived as string) ?? "0"}
                                onChange={(event) =>
                                  dispatch(
                                    setNewValue({
                                      lineId: split.id,
                                      parentLineId: row.id,
                                      field: "quantityReceived",
                                      value: event.target.value,
                                    })
                                  )
                                }
                                sx={{
                                  marginTop: "0px",
                                  marginBottom: "0px",
                                  "& .MuiInputBase-root": {
                                    height: "34px",
                                    borderRadius: "7px",
                                    backgroundColor: "rgba(255,255,255,0.95)",
                                  },
                                  "& .MuiInputLabel-root": {
                                    display: "none",
                                  },
                                }}
                              />
                            ) : (
                              <strong>{split.quantityReceived ?? "0"}</strong>
                            )}
                          </DeliveryPill>
                          <DeliveryPill $wide>
                            <span>Delivery notes</span>
                            {deliveryMode ? (
                              <TextInput
                                label="Split notes"
                                type="text"
                                id={`Split-Delivery-Note-input-${row.id}-${split.id}`}
                                value={(split.deliveryNotes as string) ?? ""}
                                onChange={(event) =>
                                  dispatch(
                                    setNewValue({
                                      lineId: split.id,
                                      parentLineId: row.id,
                                      field: "deliveryNotes",
                                      value: event.target.value,
                                    })
                                  )
                                }
                                sx={{
                                  marginTop: "0px",
                                  marginBottom: "0px",
                                  "& .MuiInputBase-root": {
                                    minHeight: "34px",
                                    borderRadius: "7px",
                                    backgroundColor: "rgba(255,255,255,0.95)",
                                  },
                                  "& .MuiInputLabel-root": {
                                    display: "none",
                                  },
                                }}
                              />
                            ) : (
                              <strong>{split.deliveryNotes || "-"}</strong>
                            )}
                          </DeliveryPill>
                        </DeliveryPillRow>
                      </OrderProductNameContainer>
                    </OrderProductContainer>
                  </SplitCard>
                ))}
              </SplitGroup>
            )}
          </OrderProductNameContainer>
        </OrderProductContainer>
      ),
    },
    quantity: {
      hidden: true,
    },
    currentStockLevel: {
      hidden: true
    },
    openPricing: {
      hidden: true
    },
    costPrice: {
      hidden: true
    },
    supplierName: {
      hidden: true
    },
  };

  if (deliveryMode) {
    Object.assign(columnConfig, {
      quantityReceived: { hidden: true },
      deliveryNotes: { hidden: true },
      notes: { hidden: true },
    });
  } else {
    Object.assign(columnConfig, {
      notes: { hidden: true },
      quantityReceived: { hidden: true },
      deliveryNotes: { hidden: true },
    });
  }
  return (
    <OrderContainer>
      {deliveryMode && (
        <DeliveryModePanel>
          <DeliveryModePanelTitle>
            <GoogleIcon id={ICON_ID.Info} isSecondary />
            Delivery Mode
          </DeliveryModePanelTitle>
          <DeliveryModePanelText>
            Update delivered quantities and notes directly in the table, then save as draft or complete the order.
          </DeliveryModePanelText>
        </DeliveryModePanel>
      )}
      <TableContainer>
        <TableView
          id="ordersTable"
          emptyText="Hmmmm, you seem to have no items in your order, this must be an error"
          fetchData={(fetchParams) => fetchMoreData(fetchParams)}
          totalCount={order.orderLines?.count ?? 0}
          data={order.orderLines?.orderLines ?? []}
          columnConfig={columnConfig}
          preLoadedData={true}
          showHeaders={true}
          search={true}
          cardRows={true}
          groupChildrenInCardRows={true}
        />
      </TableContainer>
    </OrderContainer>
  );
}
const OrderContainer = styled.div`
  width: 100%;
  margin: 0;
  height: 100%;
`;

const DeliveryModePanel = styled.div`
  display: grid;
  gap: 4px;
  margin: 6px 0 12px;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid ${(props) => props.theme.application.border};
  background: linear-gradient(90deg, rgba(236, 249, 242, 0.95) 0%, rgba(250, 255, 252, 0.98) 100%);
`;

const DeliveryModePanelTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  font-weight: 600;
`;

const DeliveryModePanelText = styled.div`
  font-size: 0.8rem;
  color: ${(props) => props.theme.application.scheme.neutral};
`;

const OrderProductContainer = styled(ProductContainer)`
  width: 100%;
  align-items: flex-start;
`;

const OrderProductNameContainer = styled(ProductNameContainer)`
  flex: 1;
  min-width: 0;
  width: 100%;
`;

const MetaPillGroup = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 10px;
  width: 100%;
`;

const MetaPill = styled.div`
  display: grid;
  gap: 4px;
  min-width: 112px;
  padding: 8px 10px;
  border-radius: 9px;
  border: 1px solid ${(props) => props.theme.application.border};
  background: rgba(255, 255, 255, 0.7);

  span {
    font-size: 0.7rem;
    text-transform: uppercase;
    color: ${(props) => props.theme.application.scheme.neutral};
    letter-spacing: 0.03em;
  }

  strong {
    font-size: 0.84rem;
    line-height: 1.2;
  }
`;

const DeliveryPill = styled.div<{ $wide?: boolean }>`
  display: grid;
  gap: 4px;
  min-width: ${(props) => (props.$wide ? "220px" : "128px")};
  flex: ${(props) => (props.$wide ? "1 1 300px" : "0 1 auto")};
  padding: 8px 10px;
  border-radius: 9px;
  border: 1px solid ${(props) => props.theme.application.border};
  background: rgba(255, 255, 255, 0.7);

  span {
    font-size: 0.7rem;
    text-transform: uppercase;
    color: ${(props) => props.theme.application.scheme.neutral};
    letter-spacing: 0.03em;
  }

  strong {
    font-size: 0.84rem;
    line-height: 1.2;
    color: ${(props) => props.theme.application.scheme.primary};
    font-weight: 600;
    white-space: break-spaces;
  }
`;

const DeliveryPillRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const SplitGroup = styled.div`
  display: grid;
  gap: 8px;
  margin-top: 10px;
  padding-left: 10px;
  border-left: 2px solid ${(props) => props.theme.application.border};
`;

const SplitGroupTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${(props) => props.theme.application.scheme.neutral};
`;

const SplitCard = styled.div`
  display: grid;
  gap: 8px;
  padding: 10px;
  border: 1px solid ${(props) => props.theme.application.border};
  border-radius: 6px;
  background: linear-gradient(180deg, rgba(247, 250, 251, 0.98) 0%, rgba(243, 247, 249, 0.98) 100%);
`;

const SplitMarker = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  width: fit-content;
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-radius: 999px;
  padding: 2px 10px;
  border: 1px solid ${(props) => props.theme.application.border};
  background: rgba(255, 255, 255, 0.85);
`;

